import {ADMIN_ROLE, SUPER_ADMIN_ROLE} from 'pwm-shared/src/AccessControl';
import {Bids} from 'pwm-shared/src/api/BidApi';
import {Contracts} from 'pwm-shared/src/api/ContractsApi';
import {ReactNode, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useAuth} from './hooks/use-auth';
import {tokens} from './locales/tokens';
import {paths} from './paths';

export interface PWMSideNavItem {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: PWMSideNavItem[];
  label?: ReactNode;
  path?: string;
  title: string;
}

export interface PWMSideNavSection {
  items: PWMSideNavItem[];
  subheader?: string;
}

export const usePWMNavSections = () => {
  const {t} = useTranslation();
  const {account} = useAuth();
  return useMemo((): PWMSideNavSection[] => {

    if (!account) return [];
    const adminTabs: PWMSideNavSection[] = [];
    if (account && [SUPER_ADMIN_ROLE, ADMIN_ROLE].includes(account?.role)) {
      adminTabs.push({
        subheader: t(tokens.nav.admin)!,
        items: [{
          title: t(tokens.nav.appusers),
          path: paths.authenticated.app.users
        }, {
          title: t(tokens.nav.appsettings),
          path: paths.authenticated.settings.index
        }]
      });
    }

    const availableBidItems = {
      create: {
        title: t(tokens.nav.bidnew),
        path: paths.authenticated.bids.bidnew
      },
      list: {
        title: t(tokens.nav.bidlist),
        path: paths.authenticated.bids.index
      }
    };

    const availableContractItems = {
      create: {
        title: t(tokens.nav.contractnew),
        path: paths.authenticated.contracts.contractnew
      },
      list: {
        title: t(tokens.nav.contractlist),
        path: paths.authenticated.contracts.index
      }
    };

    const moduleTabs: PWMSideNavSection[] = [];
    const bidItems = new Set<PWMSideNavItem>();
    const contractItems = new Set<PWMSideNavItem>();
    if([SUPER_ADMIN_ROLE].includes(account?.role)){
      Object.values(availableBidItems).forEach((item) => bidItems.add(item));
      Object.values(availableContractItems).forEach((item) => contractItems.add(item));
    } else {
      account.userRoles.forEach((role) => {
        role.role.permissions.forEach((permission) => {
          switch (permission.applicationModule.name) {
            case Bids.SolicitationModuleName: {
              if (permission.create) {
                bidItems.add(availableBidItems.create);
              }
              if (permission.list) {
                bidItems.add(availableBidItems.list);
              }
              break;
            }
            case Contracts.ModuleName: {
              /*if (permission.create) { // at this time only bids can create a contract
                contractItems.add(availableContractItems.create);
              }*/
              if (permission.list) {
                contractItems.add(availableContractItems.list);
              }
              break;
            }
          }
        });
      });
    }
    if (bidItems.size != 0) {
      moduleTabs.push({
        subheader: t(tokens.nav.bidlist)!,
        items: Array.from(bidItems)
      });
    }

    if (contractItems.size != 0) {
      moduleTabs.push({
        subheader: t(tokens.nav.contractlist)!,
        items: Array.from(contractItems)
      });
    }

    return [{
      subheader: t(tokens.nav.pwm)!,
      items: [{
        title: t(tokens.nav.home),
        path: paths.authenticated.home
      },
        {
          title: t(tokens.nav.vendorsearch),
          path: paths.authenticated.vendors.index
        }]
    },
      ...adminTabs,
      ...moduleTabs,
      {
        subheader: t(tokens.nav.reports)!,
        items: [{
          title: t(tokens.nav.list),
          path: paths.authenticated.reports.index
        }/*,
          {
            title: t(tokens.nav.reportdesigner),
            path: paths.authenticated.reports.index
          }*/]
      }];
  }, [account, t]);
};